import * as DialogPrimitive from "@radix-ui/react-dialog";
import { observer } from "mobx-react-lite";
import { memo, useCallback, useMemo } from "react";
import {
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Layers3,
  Search,
  X,
} from "lucide-react";
import { OrgTreeSelect } from "@/components/common/OrgTreeSelect";
import { SelectField } from "@/components/common";
import { CategoryBadge, MetricCodeBadge } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTran } from "@/hooks";
import { getUnitTypeMeta } from "@/features/organization/utils/organization.helpers";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import OrgBreadcrumb, {
  useOrgPathWithStatus,
} from "@/components/common/OrgBreadcrumb";
import { buildOrgStatusInfoMap } from "@/components/common/organization.utils";
import { cn } from "@/lib/utils";
import { useDashboardFiltersStore } from "../hooks/useDashboardStore";
import { useAssignedMetricsDrawerData } from "../hooks/useAssignedMetricsDrawerData";
import { useAssignedMetricsDrawerFilters } from "../hooks/useAssignedMetricsDrawerFilters";
import {
  buildFrameworkLabelByCode,
  getFrameworkLabel,
} from "../utils/reporting-years";
import {
  CATEGORY_ORDER,
  DEFAULT_PAGE_SIZE,
  formatCount,
  getAssignedMetricsGroupKey,
  GROUP_BY_OPTIONS,
  PAGE_SIZE_OPTIONS,
} from "../utils/assigned-metrics-drawer";

const FILTER_CONTROL_CLASS = "w-full";

const FrameworkBadge = memo(({ framework, frameworkLabelByCode }) => {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground shadow-sm">
      <Layers3 className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="max-w-[160px] truncate">
        {getFrameworkLabel(framework, frameworkLabelByCode)}
      </span>
    </span>
  );
});

const NeutralBadge = memo(({ children, className }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground",
      className,
    )}
  >
    {children}
  </span>
));

const SummaryPill = memo(({ category, metrics, assignments }) => (
  <div className="inline-flex min-w-[172px] items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-2 shadow-sm">
    <CategoryBadge category={category} />
    <div className="min-w-0">
      <p className="truncate text-[12px] font-semibold text-foreground">
        {formatCount(metrics)} metrics
      </p>
      <p className="text-[11px] text-muted-foreground">
        {formatCount(assignments)} assignments
      </p>
    </div>
  </div>
));

const ViewDetailButton = memo(
  ({ isOpen, onClick, label, disabled = false }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-1.5 text-[11px] font-medium text-foreground shadow-sm transition hover:border-border hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
    >
      {label}
      <ChevronDown
        className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")}
      />
    </button>
  ),
);

const DetailLoadingState = memo(() => (
  <div className="space-y-3">
    {Array.from({ length: 2 }).map((_, index) => (
      <div
        key={index}
        className="animate-pulse rounded-lg border border-border/70 bg-muted p-3"
      >
        <div className="space-y-3">
          <div className="h-4 w-2/5 rounded-full bg-background" />
          <div className="h-4 w-4/5 rounded-full bg-background" />
          <div className="flex gap-2">
            <div className="h-6 w-20 rounded-full bg-background" />
            <div className="h-6 w-24 rounded-full bg-background" />
          </div>
        </div>
      </div>
    ))}
  </div>
));

const SummaryCardSkeleton = memo(() => (
  <div className="animate-pulse rounded-lg border border-border/70 bg-muted px-3 py-3 shadow-sm">
    <div className="h-6 w-3/4 rounded-full bg-background" />
    <div className="mt-2 h-4 w-5/6 rounded-full bg-background" />
  </div>
));

const CategorySummarySkeleton = memo(() => (
  <div className="flex flex-wrap gap-3">
    {Array.from({ length: CATEGORY_ORDER.length }).map((_, index) => (
      <div
        key={index}
        className="h-12 w-[180px] animate-pulse rounded-full border border-border/70 bg-card shadow-sm"
      />
    ))}
  </div>
));

const SectionError = memo(({ message, onRetry, t }) => (
  <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <span>{message}</span>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center justify-center rounded-full border border-destructive/30 bg-background px-4 py-2 text-sm font-medium text-destructive transition hover:border-destructive/40"
      >
        {t("Try again")}
      </button>
    </div>
  </div>
));

const LoadingState = memo(() => (
  <div className="space-y-4">
    {Array.from({ length: 4 }).map((_, index) => (
      <div
        key={index}
        className="animate-pulse rounded-lg border border-border/70 bg-card px-3 py-3 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-muted" />
          <div className="min-w-0 flex-1 space-y-3">
            <div className="h-5 w-2/5 rounded-full bg-muted" />
            <div className="flex flex-wrap gap-2">
              <div className="h-7 w-20 rounded-full bg-muted" />
              <div className="h-7 w-28 rounded-full bg-muted" />
              <div className="h-7 w-24 rounded-full bg-muted" />
            </div>
            <div className="h-4 w-1/2 rounded-full bg-muted" />
          </div>
          <div className="h-10 w-28 rounded-full bg-muted" />
        </div>
      </div>
    ))}
  </div>
));

const EmptyState = memo(({ title, description }) => (
  <div className="rounded-lg border border-dashed border-border/70 bg-muted px-4 py-8 text-center">
    <p className="text-sm font-semibold text-foreground">{title}</p>
    <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
      {description}
    </p>
  </div>
));

const OrganizationIconBadge = memo(({ unitLevel, className }) => {
  const meta = getUnitTypeMeta(unitLevel);
  const Icon = meta.icon || Building2;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border",
        meta.color,
        className,
      )}
    >
      <Icon className="h-4 w-4" />
    </span>
  );
});

const MetricDetailCard = memo(
  ({ assignment, orgStatus, orgInactiveDate, orgTree }) => {
    const { pathWithStatus } = useOrgPathWithStatus(
      assignment.organizationId,
      orgTree,
    );

    return (
      <div className="rounded-lg border border-border/70 bg-muted px-3 py-3 shadow-sm">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <OrganizationIconBadge
              unitLevel={assignment.organizationUnitLevel}
              className="mt-0.5 h-8 w-8"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="truncate text-sm font-semibold text-foreground">
                  {assignment.organizationName}
                </p>
                <OrgStatusTag
                  status={orgStatus}
                  inactiveDate={orgInactiveDate}
                  className="text-[10px] shrink-0"
                />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {pathWithStatus && pathWithStatus.length > 0 ? (
                  <OrgBreadcrumb pathWithStatus={pathWithStatus} />
                ) : (
                  <NeutralBadge className=" text-[10px]">
                    {assignment.organizationPathLabel}
                  </NeutralBadge>
                )}
                <NeutralBadge>{assignment.year}</NeutralBadge>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

const OrganizationMetricRow = memo(({ assignment, frameworkLabelByCode }) => (
  <div className="rounded-lg border border-border/70 bg-muted px-3 py-3 shadow-sm">
    <div className="flex flex-col gap-2.5 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/70 bg-background text-primary shadow-sm">
          <Layers3 className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">
            {assignment.metricTitle}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <MetricCodeBadge
              code={assignment.metricCode}
              category={assignment.category}
            />
            <NeutralBadge>{assignment.year}</NeutralBadge>
            <FrameworkBadge
              framework={assignment.framework}
              frameworkLabelByCode={frameworkLabelByCode}
            />
            <CategoryBadge category={assignment.category} />
          </div>
        </div>
      </div>
    </div>
  </div>
));

const YearAssignmentRow = memo(
  ({ assignment, frameworkLabelByCode, orgStatus, orgInactiveDate }) => (
    <div className="rounded-lg border border-border/70 bg-muted px-3 py-3 shadow-sm">
      <div className="flex flex-col gap-2.5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/70 bg-background text-primary shadow-sm">
            <Layers3 className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              {assignment.metricTitle}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <MetricCodeBadge
                code={assignment.metricCode}
                category={assignment.category}
              />
              <FrameworkBadge
                framework={assignment.framework}
                frameworkLabelByCode={frameworkLabelByCode}
              />
              <div className="inline-flex items-center gap-1">
                <NeutralBadge className=" text-[10px]">
                  {assignment.organizationName}
                </NeutralBadge>
                <OrgStatusTag
                  status={orgStatus}
                  inactiveDate={orgInactiveDate}
                  className="text-[10px] shrink-0"
                />
              </div>
              <CategoryBadge category={assignment.category} />
            </div>
          </div>
        </div>
      </div>
    </div>
  ),
);

const DetailSection = memo(
  ({
    detailState,
    emptyMessage,
    onRetry,
    onPageChange,
    onPageSizeChange,
    t,
    renderItem,
  }) => {
    if (detailState?.loading) return <DetailLoadingState />;

    if (detailState?.error) {
      return (
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>{detailState.error}</span>
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center justify-center rounded-full border border-destructive/30 bg-background px-4 py-2 text-sm font-medium text-destructive transition hover:border-destructive/40"
            >
              {t("Try again")}
            </button>
          </div>
        </div>
      );
    }

    if (!detailState?.loaded) return null;

    if (!Array.isArray(detailState.items) || detailState.items.length === 0) {
      return (
        <div className="rounded-lg border border-dashed border-border/70 bg-muted px-4 py-6 text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      );
    }

    return (
      <div className="space-y-2.5">
        <DetailPaginationBar
          page={detailState.page}
          pageSize={detailState.pageSize}
          total={detailState.total}
          totalPages={detailState.totalPages}
          disabled={detailState.loading}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          t={t}
        />
        {detailState.items.map(renderItem)}
      </div>
    );
  },
);

const DetailPaginationBar = memo(
  ({
    page,
    pageSize,
    total,
    totalPages,
    onPageChange,
    onPageSizeChange,
    disabled,
    t,
  }) => {
    if (!total) return null;

    return (
      <div className="flex flex-col gap-2 rounded-lg border border-border/70 bg-background px-3 py-2 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">
            {t("Items per page")}
          </span>
          <SelectField
            options={PAGE_SIZE_OPTIONS}
            value={String(pageSize || DEFAULT_PAGE_SIZE)}
            onValueChange={onPageSizeChange}
            sortOptions={false}
            disabled={disabled}
            disablePortal
            className="space-y-0"
            selectClassName="h-8 w-[80px] rounded-lg border-border/70 bg-card text-xs"
          />
          <span className="text-[11px] text-muted-foreground">
            {`${formatCount(total)} ${t("items")}`}
          </span>
        </div>

        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground">
            {`${t("Page")} ${page} ${t("of")} ${totalPages}`}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={disabled || page <= 1}
              className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-card px-3 py-1.5 text-[11px] font-medium text-muted-foreground transition hover:border-border hover:text-foreground disabled:cursor-not-allowed disabled:opacity-45"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {t("Previous")}
            </button>
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={disabled || page >= totalPages}
              className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-card px-3 py-1.5 text-[11px] font-medium text-muted-foreground transition hover:border-border hover:text-foreground disabled:cursor-not-allowed disabled:opacity-45"
            >
              {t("Next")}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  },
);

const GroupCardShell = memo(
  ({
    icon,
    iconClassName,
    title,
    subtitle,
    badges,
    isOpen,
    onToggle,
    detailState,
    onRetry,
    onDetailPageChange,
    onDetailPageSizeChange,
    emptyMessage,
    renderItem,
    t,
  }) => {
    const IconComponent = icon;

    return (
      <div className="rounded-lg border border-border/70 bg-card px-3 py-3 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span
              className={cn(
                "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary",
                iconClassName,
              )}
            >
              <IconComponent className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-5 text-foreground">
                {title}
              </p>
              {badges ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {badges}
                </div>
              ) : null}
              <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
                {subtitle}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 justify-end">
            <ViewDetailButton
              isOpen={isOpen}
              onClick={onToggle}
              label={t("View Detail")}
              disabled={detailState?.loading}
            />
          </div>
        </div>

        {isOpen ? (
          <div className="mt-3 space-y-2.5 border-t border-border/70 pt-3">
            <DetailSection
              detailState={detailState}
              emptyMessage={emptyMessage}
              onRetry={onRetry}
              onPageChange={onDetailPageChange}
              onPageSizeChange={onDetailPageSizeChange}
              t={t}
              renderItem={renderItem}
            />
          </div>
        ) : null}
      </div>
    );
  },
);

const MetricGroupCard = memo(
  ({
    row,
    isOpen,
    onToggle,
    detailState,
    onRetry,
    onDetailPageChange,
    onDetailPageSizeChange,
    frameworkLabelByCode,
    orgStatusMap,
    orgTree,
    t,
  }) => (
    <GroupCardShell
      icon={Layers3}
      title={row.metricTitle}
      subtitle={`Assigned to ${formatCount(row.organizationCount)} organizations across ${formatCount(row.yearCount)} years - ${formatCount(row.assignmentCount)} assignments`}
      badges={
        <>
          <MetricCodeBadge code={row.metricCode} category={row.category} />
          <FrameworkBadge
            framework={row.framework}
            frameworkLabelByCode={frameworkLabelByCode}
          />
          {/* <NeutralBadge>
            <Building2 className="h-3.5 w-3.5" />
            {row.organizationGroupLabel}
          </NeutralBadge> */}
          <CategoryBadge category={row.category} />
        </>
      }
      isOpen={isOpen}
      onToggle={onToggle}
      detailState={detailState}
      onRetry={onRetry}
      onDetailPageChange={onDetailPageChange}
      onDetailPageSizeChange={onDetailPageSizeChange}
      emptyMessage={t("No metric assignments matched the current filters.")}
      renderItem={(assignment) => {
        const info = orgStatusMap.get(String(assignment.organizationId || ""));
        return (
          <MetricDetailCard
            key={assignment.id}
            assignment={assignment}
            orgStatus={info?.status}
            orgInactiveDate={info?.inactiveDate}
            orgTree={orgTree}
          />
        );
      }}
      t={t}
    />
  ),
);

const OrganizationGroupCard = memo(
  ({
    row,
    isOpen,
    onToggle,
    detailState,
    onRetry,
    onDetailPageChange,
    onDetailPageSizeChange,
    frameworkLabelByCode,
    orgTree,
    t,
  }) => {
    const meta = getUnitTypeMeta(row.organizationUnitLevel);
    const Icon = meta.icon || Building2;

    const { pathWithStatus, orgStatus, orgInactiveDate } = useOrgPathWithStatus(
      row.organizationId,
      orgTree,
    );

    return (
      <GroupCardShell
        icon={Icon}
        title={
          <div className="flex items-center gap-1.5">
            <span>{row.organizationName}</span>
            <OrgStatusTag
              status={orgStatus}
              inactiveDate={orgInactiveDate}
              className="text-[10px] shrink-0"
            />
          </div>
        }
        subtitle={`${formatCount(row.metricCount)} metrics across ${formatCount(row.yearCount)} years - ${formatCount(row.assignmentCount)} assignments`}
        badges={
          pathWithStatus && pathWithStatus.length > 0 ? (
            <OrgBreadcrumb pathWithStatus={pathWithStatus} />
          ) : (
            <p className="text-xs leading-5 text-muted-foreground">
              {row.organizationPathLabel}
            </p>
          )
        }
        iconClassName={cn(
          "rounded-full bg-background shadow-sm ring-1 ring-border",
          meta.color,
        )}
        isOpen={isOpen}
        onToggle={onToggle}
        detailState={detailState}
        onRetry={onRetry}
        onDetailPageChange={onDetailPageChange}
        onDetailPageSizeChange={onDetailPageSizeChange}
        emptyMessage={t("No assigned metrics matched the current filters.")}
        renderItem={(assignment) => (
          <OrganizationMetricRow
            key={assignment.id}
            assignment={assignment}
            frameworkLabelByCode={frameworkLabelByCode}
          />
        )}
        t={t}
      />
    );
  },
);

const YearGroupCard = memo(
  ({
    row,
    isOpen,
    onToggle,
    detailState,
    onRetry,
    onDetailPageChange,
    onDetailPageSizeChange,
    frameworkLabelByCode,
    orgStatusMap,
    t,
  }) => (
    <GroupCardShell
      icon={CalendarDays}
      title={`Reporting year ${row.year}`}
      subtitle={`${formatCount(row.metricCount)} metrics across ${formatCount(row.organizationCount)} organizations - ${formatCount(row.assignmentCount)} assignments`}
      isOpen={isOpen}
      onToggle={onToggle}
      detailState={detailState}
      onRetry={onRetry}
      onDetailPageChange={onDetailPageChange}
      onDetailPageSizeChange={onDetailPageSizeChange}
      emptyMessage={t("No year assignments matched the current filters.")}
      renderItem={(assignment) => {
        const info = orgStatusMap.get(String(assignment.organizationId || ""));
        return (
          <YearAssignmentRow
            key={assignment.id}
            assignment={assignment}
            frameworkLabelByCode={frameworkLabelByCode}
            orgStatus={info?.status}
            orgInactiveDate={info?.inactiveDate}
          />
        );
      }}
      t={t}
    />
  ),
);

const PaginationBar = memo(
  ({
    page,
    pageSize,
    totalPages,
    onPageChange,
    onPageSizeChange,
    disabled,
    t,
  }) => (
    <div className="flex flex-col gap-3 rounded-lg border border-border/70 bg-card px-3 py-3 shadow-sm md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">
          {t("Items per page")}
        </span>
        <SelectField
          options={PAGE_SIZE_OPTIONS}
          value={String(pageSize || DEFAULT_PAGE_SIZE)}
          onValueChange={onPageSizeChange}
          sortOptions={false}
          disabled={disabled}
          disablePortal
          className="space-y-0"
          selectClassName="h-9 w-[84px] rounded-lg border-border/70 bg-background"
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground">
          {`${t("Page")} ${page} ${t("of")} ${totalPages}`}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={disabled || page <= 1}
            className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background px-3.5 py-2 text-sm font-medium text-muted-foreground transition hover:border-border hover:text-foreground disabled:cursor-not-allowed disabled:opacity-45"
          >
            <ChevronLeft className="h-4 w-4" />
            {t("Previous")}
          </button>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={disabled || page >= totalPages}
            className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background px-3.5 py-2 text-sm font-medium text-muted-foreground transition hover:border-border hover:text-foreground disabled:cursor-not-allowed disabled:opacity-45"
          >
            {t("Next")}
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  ),
);

const AssignedMetricsDrawer = observer(({ open, onOpenChange }) => {
  const { t } = useTran();
  const dashboardFilters = useDashboardFiltersStore();
  const { filters, available } = dashboardFilters;
  const frameworkLabelByCode = useMemo(
    () => buildFrameworkLabelByCode(available.frameworkObjects),
    [available.frameworkObjects],
  );
  const orgStatusMap = useMemo(
    () => buildOrgStatusInfoMap(available.orgTree || []),
    [available.orgTree],
  );
  const {
    drawerFilters,
    activeCount,
    filterOptions,
    selectedValues,
    setHierarchy,
    setOrganizationIds,
    setYear,
    setFramework,
    setCategory,
    clearFilters,
  } = useAssignedMetricsDrawerFilters({
    open,
    dashboardFilters: filters,
    dashboardFrameworkAllSelected: dashboardFilters.frameworkAllSelected,
    available,
    t,
  });
  const {
    search,
    setSearch,
    groupBy,
    setGroupBy,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    openGroups,
    toggleGroup,
    detailCache,
    retryGroupDetail,
    setGroupDetailPage,
    setGroupDetailPageSize,
    resetView,
    summary,
    summaryLoading,
    summaryError,
    retrySummary,
    categorySummary,
    categoryLoading,
    categoryError,
    retryCategory,
    listData,
    listLoading,
    listError,
    retryList,
  } = useAssignedMetricsDrawerData({
    open,
    filters: drawerFilters,
    available,
    t,
  });

  const renderGroup = useCallback(
    (row) => {
      const groupKey = getAssignedMetricsGroupKey(row);
      const isOpen = Boolean(openGroups[groupKey]);
      const detailState = detailCache[groupKey];
      const handleToggle = () => toggleGroup(row);
      const handleRetry = () => retryGroupDetail(row);
      const handleDetailPageChange = (nextPage) =>
        setGroupDetailPage(row, nextPage);
      const handleDetailPageSizeChange = (nextPageSize) =>
        setGroupDetailPageSize(row, nextPageSize);

      if (groupBy === "organization") {
        return (
          <OrganizationGroupCard
            key={groupKey}
            row={row}
            isOpen={isOpen}
            onToggle={handleToggle}
            detailState={detailState}
            onRetry={handleRetry}
            onDetailPageChange={handleDetailPageChange}
            onDetailPageSizeChange={handleDetailPageSizeChange}
            frameworkLabelByCode={frameworkLabelByCode}
            orgTree={available.orgTree}
            t={t}
          />
        );
      }

      if (groupBy === "year") {
        return (
          <YearGroupCard
            key={groupKey}
            row={row}
            isOpen={isOpen}
            onToggle={handleToggle}
            detailState={detailState}
            onRetry={handleRetry}
            onDetailPageChange={handleDetailPageChange}
            onDetailPageSizeChange={handleDetailPageSizeChange}
            frameworkLabelByCode={frameworkLabelByCode}
            orgStatusMap={orgStatusMap}
            t={t}
          />
        );
      }

      return (
        <MetricGroupCard
          key={groupKey}
          row={row}
          isOpen={isOpen}
          onToggle={handleToggle}
          detailState={detailState}
          onRetry={handleRetry}
          onDetailPageChange={handleDetailPageChange}
          onDetailPageSizeChange={handleDetailPageSizeChange}
          frameworkLabelByCode={frameworkLabelByCode}
          orgStatusMap={orgStatusMap}
          orgTree={available.orgTree}
          t={t}
        />
      );
    },
    [
      // `available` is mutated in place by the filters store, so its identity
      // never changes — depend on the captured fields, or the callback keeps
      // the empty orgStatusMap/orgTree from mount forever.
      available.orgTree,
      orgStatusMap,
      detailCache,
      frameworkLabelByCode,
      groupBy,
      openGroups,
      retryGroupDetail,
      setGroupDetailPage,
      setGroupDetailPageSize,
      t,
      toggleGroup,
    ],
  );

  const searchPlaceholder =
    groupBy === "organization"
      ? t("Search organization")
      : t("Search metric by code or title");

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-background/80 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          onPointerDownOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          className="fixed inset-y-0 right-0 z-[80] flex h-[100dvh] w-full max-w-[min(100vw,720px)] flex-col overflow-hidden border-l border-border/70 bg-background shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-right-8 data-[state=open]:duration-300 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-200"
        >
          <div className="sticky top-0 z-20 border-b border-border/70 bg-background px-4 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <DialogPrimitive.Title className="text-base font-semibold tracking-tight text-foreground">
                  {t("Assigned Metrics")}
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="mt-1 text-xs leading-5 text-muted-foreground">
                  {t(
                    "Which metrics are assigned, to which organizations, and in which years.",
                  )}
                </DialogPrimitive.Description>
              </div>

              <DialogPrimitive.Close className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground shadow-sm transition hover:border-border hover:text-foreground">
                <X className="h-5 w-5" />
                <span className="sr-only">{t("Close")}</span>
              </DialogPrimitive.Close>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <div className="flex min-h-full flex-col gap-3">
              {summaryLoading ? (
                <SummaryCardSkeleton />
              ) : summaryError ? (
                <SectionError
                  message={summaryError}
                  onRetry={retrySummary}
                  t={t}
                />
              ) : (
                <div className="rounded-lg border border-border/70 bg-muted px-3 py-3 shadow-sm">
                  <p className="text-sm font-semibold text-foreground">
                    {`${formatCount(summary.uniqueMetrics)} unique metrics - ${formatCount(summary.assignments)} assignments across organizations and years`}
                  </p>
                  <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
                    {t(
                      "Each assignment expands one metric to one organization in one reporting year within the current dashboard scope.",
                    )}
                  </p>
                </div>
              )}

              {categoryLoading ? (
                <CategorySummarySkeleton />
              ) : categoryError ? (
                <SectionError
                  message={categoryError}
                  onRetry={retryCategory}
                  t={t}
                />
              ) : (
                <div className="flex flex-wrap gap-3">
                  {categorySummary.map((bucket) => (
                    <SummaryPill
                      key={bucket.category}
                      category={bucket.category}
                      metrics={bucket.metrics}
                      assignments={bucket.assignments}
                    />
                  ))}
                </div>
              )}

              <div className="rounded-lg border border-border/70 bg-card px-3 py-3 shadow-sm">
                <div className="grid grid-cols-1 items-end gap-3 rounded-lg bg-muted/40 p-3 sm:grid-cols-2 lg:grid-cols-3">
                  <SelectField
                    label={t("Reporting Year")}
                    options={filterOptions.year}
                    value={selectedValues.year}
                    onValueChange={(value) => {
                      setYear(value);
                      resetView();
                    }}
                    sortOptions={false}
                    disablePortal
                    className={FILTER_CONTROL_CLASS}
                    contentClassName="w-[min(15rem,calc(100vw-1rem))]"
                  />
                  <SelectField
                    label={t("Filter By")}
                    options={filterOptions.hierarchy}
                    value={selectedValues.hierarchy}
                    onValueChange={(value) => {
                      setHierarchy(value);
                      resetView();
                    }}
                    sortOptions={false}
                    disablePortal
                    className={FILTER_CONTROL_CLASS}
                  />

                  <div className={`${FILTER_CONTROL_CLASS} space-y-1.5`}>
                    <label className="text-sm font-medium text-foreground">
                      {t("Organization")}
                    </label>
                    <OrgTreeSelect
                      tree={filterOptions.organizationTree}
                      value={selectedValues.organizationIds}
                      onChange={(orgIds) => {
                        setOrganizationIds(orgIds);
                        resetView();
                      }}
                      multiSelect
                      showBranchSelectAll
                      branchSelectLevels={["parent_company", "level_1"]}
                      placeholder={t("All Organizations")}
                      disablePortal
                      className="w-full"
                      allowOrgs={filterOptions.organizationIds}
                    />
                  </div>

                  <SelectField
                    label={t("Framework")}
                    options={filterOptions.framework}
                    value={selectedValues.framework}
                    onValueChange={(value) => {
                      setFramework(value);
                      resetView();
                    }}
                    sortOptions={false}
                    disablePortal
                    className={FILTER_CONTROL_CLASS}
                  />

                  <SelectField
                    label={t("Category")}
                    options={filterOptions.category}
                    value={selectedValues.category}
                    onValueChange={(value) => {
                      setCategory(value);
                      resetView();
                    }}
                    sortOptions={false}
                    disablePortal
                    className={FILTER_CONTROL_CLASS}
                  />

                  {activeCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        clearFilters();
                        resetView();
                      }}
                      className="w-auto justify-self-start gap-1.5"
                    >
                      <X size={14} />
                      {t("Clear")}
                      <Badge
                        variant="secondary"
                        className="ml-1 px-1.5 py-0 text-[10px] font-semibold"
                      >
                        {activeCount}
                      </Badge>
                    </Button>
                  )}
                </div>

                <div className="relative mt-3">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={searchPlaceholder}
                    className="h-10 rounded-lg border-border/70 bg-background pl-11 text-sm text-foreground shadow-none placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">
                    {t("Group By")}
                  </span>
                  <div className="inline-flex rounded-xl border border-border/70 bg-card p-1 shadow-sm">
                    {GROUP_BY_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setGroupBy(option.value)}
                        className={cn(
                          "rounded-lg px-3.5 py-1.5 text-sm font-medium transition",
                          groupBy === option.value
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {t(option.label)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <PaginationBar
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                disabled={listLoading}
                onPageChange={(nextPage) =>
                  setPage(Math.min(Math.max(nextPage, 1), totalPages))
                }
                onPageSizeChange={setPageSize}
                t={t}
              />

              {listError ? (
                <SectionError message={listError} onRetry={retryList} t={t} />
              ) : null}

              {listLoading && listData.rows.length === 0 ? (
                <LoadingState />
              ) : !listError && listData.total === 0 ? (
                <EmptyState
                  title={t("No assigned metrics match these filters")}
                  description={t(
                    "Adjust the search query to inspect a different slice of the current dashboard assignment scope.",
                  )}
                />
              ) : !listError ? (
                <div className="space-y-3">
                  {listData.rows.map(renderGroup)}
                </div>
              ) : null}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
});

export default AssignedMetricsDrawer;
